import {
  BaJwt,
  isSupportedAccountType,
  LoginEventPayload,
  OiamEvents,
  SetConfigEventPayload,
  TokenEnsurer,
} from '@oiambk/oiam-oauth-types';
import log from 'loglevel';
import { SigninRedirectArgs, User, UserManager, UserManagerSettings, WebStorageStateStore } from 'oidc-client-ts';
import { Config } from '../model/config';
import { ERR_NO_USER, ERR_NOT_CONFIGURED, OiamErrorPayloadException } from '../model/errors';
import { LoginStep } from '../model/necessary-login';
import { TokenStorageFactory } from '../storage/token-storage-factory';
import { buildAcrValues } from './acr-fns';
import { EServiceConfig } from './eservice-config';
import { FeatureToggle } from './feature-toggle';
import { OauthEventService } from './oauth-event-service';
import { StoreService } from './store-service';
import { createUserManager } from './user-manager-factory';
import { stripSpecialQueryparams } from './utils';

export class OauthService {
  private static defaultConfig: Config;
  private static userManager: UserManager;
  private static redirecturi: string;

  /**
   * Special authentication meachanism to act as a replacement for OIAM's cookies.
   * To be sent as query params to OIAM.
   */
  private static motp?: string;

  /**
   * Laden der Konfigurationsdatei
   * Zurücksetzen der Objekte
   * Auslesen von Query Parametern
   */
  public static async init(config: Config) {
    log.setLevel('info');
    this.defaultConfig = config;

    this.defaultConfig.logLevel && log.setLevel(this.defaultConfig.logLevel);
  }

  public static async setConfig(config: Omit<SetConfigEventPayload, keyof LoginEventPayload>) {
    config.logLevel && log.setLevel(config.logLevel);

    FeatureToggle.init(config);

    // Set motp, correlation-id ...
    let extraqueryparams: any = this.computeExtraQueryParams();

    // save settings for usage after redirects
    this.redirecturi = stripSpecialQueryparams(config.redirecturi, extraqueryparams);

    const settings = this.fillSettings(config, extraqueryparams);

    // factory method overwritten in e2e tests
    this.userManager = createUserManager(settings, this.defaultConfig.devmode && (config as any).e2etest);
    StoreService.setSettings(settings.client_id, settings.authority);
  }

  static checkConfigured(): boolean {
    return !!this.userManager;
  }

  static isPrevInvNecessary(): boolean {
    const params = new URLSearchParams(window.location.search);
    for (const qp of ['code', 'state', 'acr_values', 'session_state']) {
      if (params.has(qp)) {
        return true;
      }
    }
    return false;
  }

  public static async doLogin(loginStep: LoginStep, eServiceConfig: EServiceConfig) {
    await OauthEventService.sendBeforeNavigationEventWithWait();
    if (!this.checkConfigured()) {
      // page not loaded completely
      // hard reload to prevent empty page
      window.location.reload();
      return;
    }
    // When the prevInv is not necessary, we can ignore it to prevent wrong customstates on the same client-id
    const prevInv = this.isPrevInvNecessary() ? StoreService.getPreviousInvocation() : null;
    if (!prevInv) {
      this.handleWithoutPrevInv(eServiceConfig, loginStep);
    } else {
      this.handleWithPrevInv(eServiceConfig, loginStep, prevInv);
    }

    // init authorization code flow with arguments if any
    const args = await this.fillLoginArgs(loginStep, eServiceConfig);

    if (args) {
      log.debug('oiam-oauth-wc: Start login with arguments', args);
      await this.userManager.signinRedirect(args);
    } else {
      await this.userManager.signinRedirect();
    }
  }

  private static handleWithPrevInv(eServiceConfig: EServiceConfig, loginStep: "(re-)login" | "profile-selection" | "account-link", prevInv: LoginEventPayload) {
    if (loginStep === '(re-)login') {
      // Normally, in authCodeRequested the prevInv has been restored.
      // But in case of reauth_required, we restart after a logout
      // where prevInv in all other cases has been cleared.
      eServiceConfig.mergeLoginAcrValues(prevInv);
    }
    this.forgetOnetimePartsOfPreviousInvocation(loginStep, prevInv);
  }

  private static handleWithoutPrevInv(eServiceConfig: EServiceConfig, loginStep: "(re-)login" | "profile-selection" | "account-link") {
    const newPrev: LoginEventPayload = OauthService.toPreviousInvocation(eServiceConfig);
    if (loginStep === 'profile-selection') {
      // a forced profile selection should be remembered after a login,
      // but is done when sending to profile selection.
      delete newPrev.forceProfileSelection;
    }
    StoreService.setPreviousInvocation(newPrev);
    StoreService.setRetryInvocation(newPrev);
  }

  public static async doLogout() {
    if (!this.checkConfigured()) {
      throw new OiamErrorPayloadException({ ...ERR_NOT_CONFIGURED, details: 'call doLogout' });
    }
    await OauthEventService.sendBeforeNavigationEventWithWait();
    await this.doSignout();
  }

  public static setLoginMotp(motp: string) {
    this.motp = motp;
  }

  public static async askForToken(forcerenew: boolean = false): Promise<any> {
    if (!this.checkConfigured()) {
      return '';
    }
    const user = await this.getUser();
    if (!user) {
      return '';
    }
    const baJwt = new BaJwt(user.access_token);
    if (!forcerenew && TokenEnsurer.validLongEnough(baJwt)) {
      return user.access_token;
    }

    await this.userManager.signinSilent({
      extraTokenParams: { 'correlation-id': StoreService.getInstance().instance.id },
    });

    return (await this.getUser()).access_token; // has to be fresh
  }

  static async getToken(): Promise<User> {
    if (this.checkConfigured()) {
      // get the access token
      let error = '';
      const user: User = await this.userManager.signinRedirectCallback().catch(err => {
        error = err;
        return undefined;
      });
      if (user) {
        // BundID on mobile requires that the user switches to AusweisApp and on return,
        // will end up in a fresh tab on redirecturi, previousInvocation lost.
        // Therefore restore it from the user's data/state attribute.
        // Only overwrite it in this case and return as if it always had been there.
        const storedPreviousInvocation = StoreService.getPreviousInvocation();
        if (!storedPreviousInvocation) {
          const previousInvocation: LoginEventPayload = user.state;
          StoreService.setPreviousInvocation(previousInvocation);
        }
        // There is no OAG on test.dev.ocp.webapp.idst.ibaintern.de and therefor no BA-SessionId cookie
        // To prevent errors in that case, store the cookie at client side (login)
        StoreService.storeOiamSessionIdCookieOnTestDevOrLocalhost(user.access_token);

        return user;
      } else {
        throw new OiamErrorPayloadException({ ...ERR_NO_USER, details: error });
      }
    } else {
      throw new OiamErrorPayloadException({ ...ERR_NOT_CONFIGURED, details: 'call getToken' });
    }
  }

  static async getTokenEndpoint(): Promise<string> {
    if (this.checkConfigured()) {
      return await this.userManager.metadataService.getTokenEndpoint();
    }
    return '';
  }

  static async getUser() {
    if (this.checkConfigured()) {
      return this.userManager.getUser();
    }
    return null;
  }

  static async removeUser() {
    if (this.checkConfigured()) {
      return this.userManager.removeUser();
    }
    StoreService.deletePreviousInvocation();
    StoreService.deleteRetryInvocation();
  }

  static getUserManager() {
    return this.userManager;
  }

  public static getProfileSelectionRedirectUrls() {
    if (this.checkConfigured()) {
      return this.defaultConfig.profileSelectionRedirectUrls;
    } else {
      throw new OiamErrorPayloadException({ ...ERR_NOT_CONFIGURED, details: 'call getProfileSelectionRedirectUrls' });
    }
  }

  public static getRedirecturi(): string {
    if (this.checkConfigured()) {
      return this.redirecturi;
    } else {
      throw new OiamErrorPayloadException({ ...ERR_NOT_CONFIGURED, details: 'call getRedirecturi' });
    }
  }

  public static async isPendingCodeFlow(state: string): Promise<boolean> {
    const storedstate = await this.userManager.settings.stateStore.get(state);
    log.debug('oiam-oauth-wc: storedState', storedstate);
    return !!storedstate;
  }

  public static async removePendingCodeFlow(state: string): Promise<void> {
    const storedstate = await this.userManager.settings.stateStore.get(state);
    if (storedstate) {
      await this.userManager.settings.stateStore.remove(state);
      log.debug('oiam-oauth-wc: removed pending code flow', state);
    } else {
      log.debug('oiam-oauth-wc: no pending code flow which could be removed', state);
    }
  }

  public static unlinkFromClones() {
    // Remove previous user to unlink clones if necessary
    TokenStorageFactory.getInstanz().unlinkFromClones();
  }

  // ### private methods ########################################################

  private static async fillLoginArgs(
    loginStep: LoginStep,
    eServiceConfig: EServiceConfig,
  ): Promise<SigninRedirectArgs> {
    // set login-filter if not logged in (normally state empty)
    // but if there is a forcedActingOnBehalfOf we came from a logged in context already
    // we can set the acting-type / acting-on-behalf-of on the first roundtrip to keycloak and save a roundtrip

    // Override forcedActingOnBehalfOf with user jwt acting-on-behalf-of if forceJwtUpdate

    const user = await this.getUser();
    if (eServiceConfig.forceJwtUpdate) {
      // ignore if no user (not logged in already)
      if (user) {
        const baJwt = new BaJwt(user.access_token);
        const actingOnBehalfOf = baJwt?.payload['acting-on-behalf-of'];

        if (actingOnBehalfOf) eServiceConfig.forcedActingOnBehalfOf = actingOnBehalfOf;
      }
    }

    const acrString = buildAcrValues(
      eServiceConfig.acrValues,
      loginStep !== 'profile-selection' && !eServiceConfig.forcedActingOnBehalfOf ? 'login-filter' : 'acting-type',
      eServiceConfig.forcedActingOnBehalfOf,
    );

    const args: { state: LoginEventPayload; acr_values?: string; prompt?: string } = {
      state: eServiceConfig.asPreviousInvocation(),
      ...(acrString ? { acr_values: acrString } : undefined),
    };

    if (loginStep === 'account-link') {
      switch (eServiceConfig.accountLink) {
        case 'passkey':
          this.userManager.settings.extraQueryParams.kc_action = 'ba-setup-passkey';
          break;
        case 'totp':
          this.userManager.settings.extraQueryParams.kc_action = 'configure-otp';
          break;
        case 'pwd':
          this.userManager.settings.extraQueryParams.kc_action = 'UPDATE_PASSWORD';
          break;
        case 'push-mfa':
          this.userManager.settings.extraQueryParams.kc_action = 'ba-push-mfa-register';
          break;
        case 'bundid':
          args.prompt = 'bundid_link';
          break;
        case 'muk':
          args.prompt = 'muk_link';
          break;
      }
    }
    // return args if not empty
    return Object.keys(args).length > 0 ? args : null;
  }

  static handleUrlAndState(state: string) {
    let strippedUrl = stripSpecialQueryparams(window.location.href, {});
    log.debug('Stripped query parameter: ' + strippedUrl);

    const u = new URL(strippedUrl);
    if (state) {
      u.searchParams.set('customstate', state);
    }

    // update query parameters in url (minus stripped ones, plus customstate)
    window.history.replaceState(null, '', u);

    // a previous customstate is not needed after dispatching a fresh customStateEvent
    StoreService.deletePreviousInvocation();
    StoreService.deleteRetryInvocation();

    if (state) {
      // custom state as oiamCustomStateEvent
      OauthEventService.doDispatchEvent(OiamEvents.CUSTOMSTATE, {
        customstate: state,
      });
    }
  }

  private static fillSettings(config: SetConfigEventPayload, queryparams: any): UserManagerSettings {
    const scope = ['openid', ...(config.scopes ?? [])].join(' ');

    return {
      authority: config.authority ? config.authority : this.defaultConfig.authority,
      client_id: config.clientid,
      client_secret: config.clientid,
      redirect_uri: this.redirecturi,
      response_type: 'code',
      scope,
      automaticSilentRenew: false,
      fetchRequestCredentials: 'include',
      post_logout_redirect_uri: config.logouturi
        ? this.stripLogoutUri(config.logouturi, true)
        : 'https://www.arbeitsagentur.de',
      metadataUrl: config.metadataUrl ? config.metadataUrl : '',
      extraQueryParams: queryparams,
      extraTokenParams: { 'correlation-id': StoreService.getInstance().instance.id },
      userStore: new WebStorageStateStore({
        store: TokenStorageFactory.getTokenStorage(config.clientid, config.authority),
      }),
    };
  }

  private static stripLogoutUri(logouturi: string, warn: boolean): string {
    const result = logouturi.split(/[?#]/)[0];
    if (warn && result !== logouturi) {
      log.info(
        `logouturi must not contain query params, these will be removed (was '${logouturi.substring(result.length)}')`,
      );
    }
    return result;
  }

  private static computeExtraQueryParams() {
    let queryparams = this.defaultConfig?.extraQueryParams ? this.defaultConfig.extraQueryParams : {};

    // set correlation id
    queryparams = { ...queryparams, 'correlation-id': StoreService.getInstance().instance.id };

    if (this.motp) {
      queryparams.motp = this.motp;
    }
    return queryparams;
  }

  private static toPreviousInvocation(eServiceConfig: EServiceConfig): LoginEventPayload {
    const newPrev: LoginEventPayload = {
      ...eServiceConfig.acrValues,
      customstate: eServiceConfig.customstate,
      ...(isSupportedAccountType(eServiceConfig.accountLink) ? { accountLink: eServiceConfig.accountLink } : undefined),
    };
    log.debug('current invocation will be the new "previous"', newPrev);
    return newPrev;
  }

  private static forgetOnetimePartsOfPreviousInvocation(loginStep: LoginStep, p: LoginEventPayload) {
    const parts = this.partsOfPreviousInvocationToForget(loginStep);
    const atLeastOneFieldToDelete = parts.some(f => f in p);
    if (!atLeastOneFieldToDelete) {
      return;
    }
    StoreService.setPreviousInvocation(p, parts);
  }

  private static partsOfPreviousInvocationToForget(loginStep: LoginStep): (keyof LoginEventPayload)[] {
    switch (loginStep) {
      case '(re-)login':
        return ['authnLevel'];
      case 'account-link':
        return ['authnLevel', 'accountLink'];
      case 'profile-selection':
        return ['authnLevel', 'accountLink', 'forceProfileSelection'];
    }
  }

  private static async doSignout() {
    /**
     * There is no OAG on test.dev.ocp.webapp.idst.ibaintern.de and therefor no BA-SessionId cookie
     * To prevent errors in that case, remove the cookie at client side (logout)
     */
    StoreService.removeOiamSessionIdCookieOnTestDevOrLocalhost();
    return this.userManager.signoutRedirect();
  }
}
